<template>
  <IntercomLayout>
    <!-- Topbar -->
    <template #topbar>
      <IntercomTopbar
        :show-search="false"
        app-name="OnePlaceHQ"
        product-name="Billing"
        :business-name="businessName"
        :user="user"
        @notifications="handleNotifications"
        @settings="handleSettings"
        @logout="handleLogout"
      />
    </template>

    <!-- Sidebar -->
    <template #sidebar>
      <IntercomSidebar :nav-sections="navSections" />
    </template>

    <!-- Main Content -->
    <div class="payments-view">
      <!-- Page Header -->
      <div class="page-header">
        <div>
          <h1 class="page-title">{{ $t('payments.title') }}</h1>
          <p class="page-subtitle">Track all payment transactions</p>
        </div>
      </div>

      <!-- Search Bar -->
      <div class="search-bar">
        <div class="search-input-wrapper">
          <svg class="search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            ref="searchInputRef"
            v-model="searchQuery"
            type="text"
            class="search-input"
            :placeholder="$t('common.search') + '...'"
            @input="handleSearchInput"
          />
          <kbd v-if="!searchQuery" class="search-shortcut-hint">/</kbd>
          <button v-if="searchQuery" class="search-clear" @click="clearSearch">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      </div>

      <!-- Summary Cards -->
      <div class="summary-cards">
        <IntercomCard hoverable>
          <div class="stat-card">
            <div class="stat-label">{{ $t('payments.title') }}</div>
            <div class="stat-value">{{ total }}</div>
          </div>
        </IntercomCard>
        <IntercomCard hoverable>
          <div class="stat-card">
            <div class="stat-label">{{ $t('payments.totalPaid') }}</div>
            <div class="stat-value">₹{{ totalPaid.toFixed(2) }}</div>
          </div>
        </IntercomCard>
      </div>

      <!-- Payments Table -->
      <IntercomCard>
        <IntercomTable
          :columns="columns"
          :data="payments"
          :loading="isLoading"
          :sort-by="sortBy"
          :sort-order="sortOrder"
          :selected-index="selectedIndex"
          hoverable
          @sort="handleSort"
        >
          <template #cell-payment_date="{ value }">
            {{ formatDate(value) }}
          </template>

          <template #cell-payment_mode="{ value }">
            <IntercomBadge :variant="getPaymentModeBadge(value)">
              {{ $t(`payments.modes.${value}`) }}
            </IntercomBadge>
          </template>

          <template #cell-amount="{ value }">
            ₹{{ parseFloat(value).toFixed(2) }}
          </template>

          <template #cell-reference_number="{ value }">
            {{ value || '-' }}
          </template>

          <template #cell-notes="{ value }">
            <span class="truncate">{{ value || '-' }}</span>
          </template>

          <template #cell-actions="{ row }">
            <div style="display: flex; gap: var(--space-2); justify-content: flex-end">
              <IntercomButton size="sm" variant="ghost" @click.stop="handleDelete(row)">
                {{ $t('common.delete') }}
              </IntercomButton>
            </div>
          </template>
        </IntercomTable>
      </IntercomCard>

      <IntercomPagination
        :offset="offset"
        :total="total"
        :limit="limit"
        @update:offset="(v) => { offset = v; fetch() }"
      />
    </div>
  </IntercomLayout>
</template>

<script setup>
import { computed, ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuth } from '@shared/composables/useAuth.js'
import { useConfirm } from '@shared/composables/useConfirm.js'
import { usePaginatedList } from '@shared/composables/usePaginatedList.js'
import { usePageShortcuts } from '@shared/composables/usePageShortcuts.js'
import { useBilling } from '../composables/useBilling.js'
import {
  IntercomLayout,
  IntercomTopbar,
  IntercomSidebar,
  IntercomButton,
  IntercomCard,
  IntercomTable,
  IntercomBadge,
  IntercomPagination,
} from '@shared/components/intercom'
import { billingNavSections } from '../config/navigation.js'
import { formatDate } from '../utils/billingUtils.js'

const { t } = useI18n()
const router = useRouter()
const { user, logout } = useAuth()
const { confirm } = useConfirm()
const { listPayments, deletePayment, businessName, fetchBusinessName } = useBilling()

const navSections = billingNavSections

// State
const {
  items: payments,
  total,
  loading: isLoading,
  offset,
  limit,
  fetch,
  resetAndFetch,
} = usePaginatedList((params) => listPayments(params))

const searchQuery = ref('')
const sortBy = ref('payment_date')
const sortOrder = ref('desc')
const searchInputRef = ref(null)

// Keyboard shortcuts for list view
const { selectedIndex } = usePageShortcuts({
  items: payments,
  onDelete: (item) => handleDelete(item),
  searchRef: searchInputRef,
})

// Computed total paid
const totalPaid = computed(() => {
  return payments.value.reduce((sum, payment) => sum + (payment.amount || 0), 0)
})

// Table columns
const columns = [
  { key: 'payment_date', title: t('payments.paymentDate'), width: '120px', sortable: true, sortKey: 'payment_date' },
  { key: 'invoice_number', title: t('invoices.invoiceNumber'), width: '140px' },
  { key: 'customer_name', title: t('invoices.customer'), width: '180px' },
  { key: 'payment_mode', title: t('payments.paymentMode'), width: '120px', sortable: true, sortKey: 'payment_mode' },
  { key: 'amount', title: t('payments.amount'), width: '120px', align: 'right', sortable: true, sortKey: 'amount' },
  { key: 'reference_number', title: t('payments.referenceNumber'), width: '140px' },
  { key: 'notes', title: t('payments.notes'), width: '200px' },
  { key: 'actions', title: 'Actions', width: '100px', align: 'right' },
]

/**
 * Get payment mode badge variant
 */
const getPaymentModeBadge = (mode) => {
  switch (mode) {
    case 'cash':
      return 'success'
    case 'card':
      return 'info'
    case 'upi':
      return 'warning'
    case 'cheque':
      return 'default'
    case 'bank_transfer':
      return 'info'
    default:
      return 'default'
  }
}

/**
 * Handle delete payment
 */
const handleDelete = async (payment) => {
  const ok = await confirm(`Delete payment of \u20B9${payment.amount.toFixed(2)}? This action cannot be undone.`, { title: 'Delete Payment', type: 'error' })
  if (!ok) {
    return
  }

  try {
    await deletePayment(payment.id)
    // TODO: Show success notification
    await fetch()
  } catch (error) {
    console.error('Failed to delete payment:', error)
    // TODO: Show error notification
  }
}

const handleSort = ({ sortBy: newSort, sortOrder: newOrder }) => {
  sortBy.value = newSort
  sortOrder.value = newOrder
  resetAndFetch({ sort_by: newSort, sort_order: newOrder })
}

const handleSearchInput = () => {
  resetAndFetch({ search: searchQuery.value, sort_by: sortBy.value, sort_order: sortOrder.value })
}

const clearSearch = () => {
  searchQuery.value = ''
  resetAndFetch({ search: '', sort_by: sortBy.value, sort_order: sortOrder.value })
}

const handleNotifications = () => {
  console.log('Notifications')
}

const handleSettings = () => {
  router.push('/settings')
}

const handleLogout = async () => {
  try {
    await logout()
    router.push('/login')
  } catch (error) {
    console.error('Logout error:', error)
    router.push('/login')
  }
}

onMounted(() => {
  fetchBusinessName()
})
</script>

<style scoped>
@import '@shared/styles/intercom-tokens.css';
@import '@shared/styles/intercom-base.css';

.payments-view {
  padding: var(--space-8);
  max-width: var(--content-max-width);
  margin: 0 auto;
}

.page-header {
  margin-bottom: var(--space-8);
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.page-title {
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-charcoal);
  letter-spacing: var(--letter-spacing-tight);
  margin: 0;
}

.page-subtitle {
  font-size: var(--font-size-base);
  color: var(--color-gray-500);
  margin-top: var(--space-1);
  margin-bottom: 0;
}

.summary-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: var(--space-5);
  margin-bottom: var(--space-8);
}

.stat-card {
  padding: var(--space-2);
}

.stat-label {
  font-size: var(--font-size-sm);
  color: var(--color-gray-500);
  font-weight: var(--font-weight-medium);
  margin-bottom: var(--space-2);
}

.stat-value {
  font-size: var(--font-size-3xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-charcoal);
}

.truncate {
  display: block;
  max-width: 200px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Search Bar */
.search-bar {
  display: flex;
  gap: var(--space-3);
  margin-bottom: var(--space-6);
}

.search-input-wrapper {
  flex: 1;
  position: relative;
  display: flex;
  align-items: center;
}

.search-icon {
  position: absolute;
  left: 14px;
  color: var(--color-gray-400);
  pointer-events: none;
}

.search-input {
  width: 100%;
  height: 44px;
  padding: 0 var(--space-10) 0 44px;
  font-size: var(--font-size-base);
  color: var(--color-charcoal);
  background: var(--color-white);
  border: 1px solid var(--color-gray-200);
  border-radius: var(--radius-lg);
  outline: none;
  transition: all var(--transition-fast);
}

.search-input:focus {
  border-color: var(--color-blue);
  box-shadow: 0 0 0 3px rgba(0, 87, 255, 0.1);
}

.search-clear {
  position: absolute;
  right: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: var(--color-gray-100);
  border: none;
  border-radius: 50%;
  cursor: pointer;
  color: var(--color-gray-500);
  transition: all var(--transition-fast);
}

.search-clear:hover {
  background: var(--color-gray-200);
  color: var(--color-gray-700);
}

@media (max-width: 768px) {
  .payments-view {
    padding: var(--space-4);
  }

  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: var(--space-4);
  }

  .page-title {
    font-size: 26px;
  }

  .summary-cards {
    grid-template-columns: 1fr;
  }
}
</style>
